/* ====== Remove cart item ====== */
if (!customElements.get("cart-remove")) {
  customElements.define(
    "cart-remove",
    class CartRemove extends HTMLElement {
      constructor() {
        super();
        this.addEventListener("click", (event) => {
          event.preventDefault();
          const cartItems = this.closest("cart-items");
          if (cartItems) {
            cartItems.updateQuantity(
              this.dataset.index,
              0,
              null,
              this.dataset.variantId,
              this.dataset.itemKey,
            );
          }
        });
      }
    },
  );
}

if (!customElements.get("cart-items")) {
  customElements.define(
    "cart-items",
    class CartItems extends HTMLElement {
      constructor() {
        super();
        this.debouncedOnChange = webvista.throttle(
          this.onChange.bind(this),
          ON_CHANGE_DEBOUNCE_TIMER,
        );
        this.addEventListener("change", this.debouncedOnChange);
        this.sectionId             = this.dataset.section;
        this.lineItemStatusElement = document.getElementById(`Cart-Line-Item-Status-${this.sectionId}`);
        this.cartItemsContainer    = document.getElementById(`Cart-Items-${this.sectionId}`);
      }

      connectedCallback() {
        this.fetchCartItems().then((cartJson) => {
          if (!cartJson) return;
          this.syncIDKMS(cartJson).then((finalResponseJson) => {
            if (finalResponseJson) {
              this.handleCartUpdate(finalResponseJson, null, null);
            }
          });
        });
      }

      get IDKMS_VARIANT_ID() {
        return 33617173905496;
      }

      onChange(event) {
        if (event.target.classList.contains("rc-gift-radio")) return;
        if (!event.target.dataset.quantityVariantId) return;

        this.updateQuantity(
          event.target.dataset.index,
          event.target.value,
          document.activeElement,
          event.target.dataset.quantityVariantId,
          event.target.dataset.itemKey,
        );
      }

      updateQuantity(line, quantity, focusableElement, variantId, itemKey) {
        showLoader();
        const qty = parseInt(quantity, 10);
        if (qty === 0) {
          this._removeItem(line, variantId, focusableElement, itemKey);
        } else {
          this._changeQuantity(line, qty, variantId, focusableElement, itemKey);
        }
      }

      /* ====== Standard quantity change ====== */
      _changeQuantity(line, quantity, variantId, focusableElement, itemKey) {
        const input = this.querySelector(`.quantity-input[data-index='${line}']`);
        const isTracked   = input?.dataset.inventoryManagement === "shopify";
        const policy      = input?.dataset.inventoryPolicy; // "deny" or "continue"
        const maxAvailable = parseInt(input?.dataset.inventoryQuantity, 10);

        if (isTracked && policy === "deny" && !isNaN(maxAvailable) && quantity > maxAvailable) {
          hideLoader();
          this.restoreLineItemQuantityInputs(line);
          const message = window["cartStrings"]["quantityError"].replace("[quantity]", maxAvailable);
          return this.showError(message);
        }

        const GHOST_VARIANT_IDS = new Set([
          Number(window.RCGiftConfig?.giftWrapVariantId),
          Number(window.RCGiftConfig?.complimentaryVariantId),
        ]);

        const buildUpdates = (cartItems) => {
          const numericVariantId = parseInt(variantId, 10);
          const mainItem =
            cartItems.find((i) => i.key === itemKey) ||
            cartItems.find((i) => !GHOST_VARIANT_IDS.has(i.variant_id) && i.variant_id === numericVariantId) ||
            null;

          if (!mainItem) return null;

          const associatedUniqueKey = mainItem.properties?.["_associated_unique_key"] || null;

          const ghosts = associatedUniqueKey
            ? cartItems.filter((item) => {
                if (item.key === mainItem.key) return false;
                const props = item.properties || {};
                const itemUniqueKey = props["_associated_unique_key"];
                
                /* ======= An item that already carries a unique key belongs to exactly one main line item — match on that, and ONLY that. 
                   Falling through to variant-id matching here is what lets a ghost (or the Add Engraving line) get pulled from a sibling line item 
                   that happens to share the same variant_id. ======= */
                if (itemUniqueKey) {
                  return itemUniqueKey === associatedUniqueKey;
                }

                return (
                  props["_associated_variant_id"] == variantId ||
                  props["_complimentary_associated_variant_id"] == variantId
                );
              })
            : [];

          const updates = { [mainItem.key]: quantity };
          for (const ghost of ghosts) updates[ghost.key] = quantity;

          return updates;
        };

        const doUpdate = (updates, isRetry = false) => {
          const hasUpdates = updates && Object.keys(updates).length > 0;
          const endpoint   = hasUpdates
            ? window["routes"]["cart_update_url"]
            : window["routes"]["cart_change_url"];

          const payload = hasUpdates
            ? { updates }
            : { line: parseInt(line, 10), quantity };

          return fetch(endpoint, {
            ...webvista.fetchConfig(),
            body: JSON.stringify({
              ...payload,
              sections:     this.getSectionsToRender().map((s) => s.section),
              sections_url: window.location.pathname,
            }),
          })
            .then((r) => r.json())
            .then((responseJson) => {
              if (responseJson.errors) {
                this.restoreLineItemQuantityInputs(line);
                return this.showError(responseJson.errors);
              }

              // Check Shopify applied the correct quantity (guards against stock caps + key rotation)
              const updatedItem  = responseJson.items?.find((i) => String(i.variant_id) === String(variantId));
              const updatedValue = updatedItem?.quantity;

              if (!isRetry && typeof updatedValue !== "undefined" && updatedValue !== quantity) {
                // Key may have rotated — retry once with a fresh cart
                return fetch("/cart.json", { headers: { Accept: "application/json" } })
                  .then((r) => r.json())
                  .then((freshCart) => doUpdate(buildUpdates(freshCart.items), true))
                  .catch(() => doUpdate(null, true));
              }

              if (typeof updatedValue !== "undefined" && updatedValue !== quantity) {
                const message = window["cartStrings"]["quantityError"].replace("[quantity]", updatedValue);
                this.restoreLineItemQuantityInputs(line);
                return this.showError(message);
              }

              // Success
              this.syncIDKMS(responseJson).then((idkmsResponseJson) => {
                this.handleCartUpdate(idkmsResponseJson || responseJson, variantId, focusableElement);
                if (typeof syncGhostQuantitiesExternal === "function") syncGhostQuantitiesExternal();
              });
            })
            .catch(() => this.showError(window["cartStrings"]["error"]))
            .finally(() => hideLoader());
        };

        // Always fetch a fresh cart to build key-based updates
        fetch("/cart.json", { headers: { Accept: "application/json" } })
          .then((r) => r.json())
          .then((freshCart) => doUpdate(buildUpdates(freshCart.items)))
          .catch(() => doUpdate(null));
      }

      /* ====== Remove item + all linked ghosts in one batched request ====== */
      _removeItem(staleLineIndex, variantId, focusableElement, itemKey) {
        const GHOST_VARIANT_IDS = new Set([
          Number(window.RCGiftConfig?.giftWrapVariantId),
          Number(window.RCGiftConfig?.complimentaryVariantId),
        ]);

        fetch("/cart.json", { headers: { Accept: "application/json" } })
          .then((r) => r.json())
          .then(async (freshCart) => {
            const numericVariantId = parseInt(variantId, 10);
            const mainItem =
              freshCart.items.find((i) => i.key === itemKey) ||
              freshCart.items.find((i) => !GHOST_VARIANT_IDS.has(i.variant_id) && i.variant_id === numericVariantId) ||
              null;
            const associatedUniqueKey = mainItem?.properties?.["_associated_unique_key"] || itemKey || null;
            const ghosts = associatedUniqueKey
              ? freshCart.items.filter((item) => {
                  if (item.key === mainItem?.key) return false;
                  const props = item.properties || {};
                  const itemUniqueKey = props["_associated_unique_key"];

                  if (itemUniqueKey) {
                    return itemUniqueKey === associatedUniqueKey;
                  }

                  return (
                    props["_associated_variant_id"] == variantId ||
                    props["_complimentary_associated_variant_id"] == variantId
                  );
                })
              : [];

            const updates = {};
            if (mainItem) updates[mainItem.key] = 0;
            for (const ghost of ghosts) updates[ghost.key] = 0;

            // Fallback to line index if no keys resolved
            const [endpoint, payload] = Object.keys(updates).length > 0
              ? [window["routes"]["cart_update_url"],  { updates }]
              : [window["routes"]["cart_change_url"],  { line: parseInt(staleLineIndex, 10), quantity: 0 }];

            return fetch(endpoint, {
              ...webvista.fetchConfig(),
              body: JSON.stringify({
                ...payload,
                sections:     this.getSectionsToRender().map((s) => s.section),
                sections_url: window.location.pathname,
              }),
            })
            .then((r) => r.json())
            .then((responseJson) => {
              if (responseJson.errors) return this.showError(responseJson.errors);
              clearBespokeSelectedData();
              return this.syncIDKMS(responseJson).then((idkmsResponseJson) => {
                this.handleCartUpdate(idkmsResponseJson || responseJson, variantId, focusableElement);
              });
            });
          })
          .catch(() => this.showError(window["cartStrings"]["error"]))
          .finally(() => hideLoader());
      }

      /* ====== IDKMS Sync ====== */
      syncIDKMS(cartJson) {
        const items = cartJson.items || [];
        const hasIDKMSProduct = items.some(
          (item) =>
            item.variant_id !== this.IDKMS_VARIANT_ID &&
            item.properties?.["_is_idkms_included"] === "true",
        );
        const idkmsLineIndex = items.findIndex((item) => item.variant_id === this.IDKMS_VARIANT_ID);
        const idkmsInCart    = idkmsLineIndex !== -1;

        if (hasIDKMSProduct && !idkmsInCart)  return this.addIDKMS();
        if (!hasIDKMSProduct && idkmsInCart)  return this.removeIDKMS(idkmsLineIndex + 1);

        return Promise.resolve(null);
      }

      addIDKMS() {
        const uniqueKey = (Math.random() + 1).toString(36).substring(2);
        return fetch(window["routes"]["cart_add_url"], {
          ...webvista.fetchConfig(),
          body: JSON.stringify({
            items: [{ id: this.IDKMS_VARIANT_ID, quantity: 1, properties: { _associated_unique_key: uniqueKey } }],
            sections:     this.getSectionsToRender().map((s) => s.section),
            sections_url: window.location.pathname,
          }),
        })
        .then((r) => r.json())
        .then((responseJson) => {
          if (responseJson.status && responseJson.status !== 200) {
            console.error("[IDKMS] Failed to add:", responseJson.description);
            return null;
          }
          return responseJson;
        })
        .catch((err) => { console.error("[IDKMS] Error adding:", err); return null; });
      }

      removeIDKMS(idkmsLine) {
        return fetch(window["routes"]["cart_change_url"], {
          ...webvista.fetchConfig(),
          body: JSON.stringify({
            line:         idkmsLine,
            quantity:     0,
            sections:     this.getSectionsToRender().map((s) => s.section),
            sections_url: window.location.pathname,
          }),
        })
        .then((r) => r.json())
        .then((responseJson) => {
          if (responseJson.errors) { console.error("[IDKMS] Failed to remove:", responseJson.errors); return null; }
          return responseJson;
        })
        .catch((err) => { console.error("[IDKMS] Error removing:", err); return null; });
      }

      fetchCartItems() {
        return fetch(`${window["routes"]["cart_url"]}.js`)
          .then((r) => r.json())
          .catch((err) => { console.error("[IDKMS] Error fetching cart:", err); return null; });
      }

      handleCartUpdate(responseJson, variantId, focusableElement) {
        SectionDynamicUpdate.updateSections(this.getSectionsToRender(), responseJson.sections);

        // Re-run price tier selection after DOM is updated
        this.applyCartPriceTiers();

        if (this.drawerTrap) {
          webvista.trapFocus(this.drawerTrap, focusableElement);
        } else if (focusableElement) {
          focusableElement.focus();
        }

        webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
          source:           "cart-items",
          productVariantId: variantId,
          cartData:         responseJson,
        });
      }

      applyCartPriceTiers() {
        const saleCode = localStorage.getItem('sale_type') || "";
        // These must match your Shopify theme settings — read from a global config object
        // or inject them once into window from Liquid (see note below)
        const { vipCode, vvipCode, showSale, showVip, showVvip } = window.RCPriceConfig || {};

        document.querySelectorAll('.rc-cart-price-wrapper[data-line-uid]').forEach((wrapper) => {
          // Read the per-unit values stored as data attributes (see Liquid change below)
          const regular = Number(wrapper.dataset.regular || 0);
          const compare = Number(wrapper.dataset.compare || 0);
          const sale    = Number(wrapper.dataset.sale    || 0);
          const vip     = Number(wrapper.dataset.vip     || 0);
          const vvip    = Number(wrapper.dataset.vvip    || 0);

          let priceToShow = null;
          if (compare !== 0 && compare > regular) {
            priceToShow = '.rc-cart-compare';
          } else if (sale !== 0 && sale < regular && showSale === true) {
            priceToShow = '.rc-cart-sale';
          } else if (vip !== 0 && vip < regular && showVip === true && saleCode === vipCode) {
            priceToShow = '.rc-cart-vip';
          } else if (vvip !== 0 && vvip < regular && showVvip === true && saleCode === vvipCode) {
            priceToShow = '.rc-cart-vvip';
          }

          // Reset all tiers first
          wrapper.querySelector('.rc-cart-regular')?.classList.remove('is-hide');
          wrapper.querySelector('.rc-cart-compare')?.classList.add('is-hide');
          wrapper.querySelector('.rc-cart-sale')?.classList.add('is-hide');
          wrapper.querySelector('.rc-cart-vip')?.classList.add('is-hide');
          wrapper.querySelector('.rc-cart-vvip')?.classList.add('is-hide');

          if (!priceToShow) return;

          const regularEl = wrapper.querySelector('.rc-cart-regular');
          const saleEl    = wrapper.querySelector(priceToShow);
          if (saleEl) {
            regularEl?.classList.add('is-hide');
            saleEl.classList.remove('is-hide');
          }
        });
      }

      async restoreLineItemQuantityInputs(line) {
        if (!line) return;
        this.querySelectorAll(`.quantity-input[data-index='${line}']`)
          .forEach((input) => { input.value = input.getAttribute("value"); });

        const html = await fetch(window.location.pathname).then((r) => r.text());
        const doc  = new DOMParser().parseFromString(html, "text/html");
        document.querySelectorAll(".cart-item-total-prices").forEach((el, i) => {
          const fresh = doc.querySelectorAll(".cart-item-total-prices")[i];
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
      }

      getSectionsToRender() {
        return [
          { id: "Cart-Icon-Bubble", section: "cart-icon-bubble",  selector: ".shopify-section" },
          { id: "Main-Cart",        section: this.sectionId,       selector: "#Main-Cart-Details" },
        ];
      }

      showError(message = null) {
        if (!message) return;
        webvista.popToast(message, "error");
      }
    },
  );
  
  function rcApplyCartPriceTiers(scope) {
    var root = scope || document;
    var wrappers = root.querySelectorAll('.rc-cart-price-wrapper');
    var saleCode = localStorage.getItem('sale_type') || '';

    wrappers.forEach(function (wrapper) {
      var regular = parseFloat(wrapper.dataset.regular) || 0;
      var compare = parseFloat(wrapper.dataset.compare) || 0;
      var sale    = parseFloat(wrapper.dataset.sale) || 0;
      var vip     = parseFloat(wrapper.dataset.vip) || 0;
      var vvip    = parseFloat(wrapper.dataset.vvip) || 0;

      var showSale = wrapper.dataset.showSale === 'true';
      var showVip  = wrapper.dataset.showVip === 'true';
      var showVvip = wrapper.dataset.showVvip === 'true';
      var vipCode  = wrapper.dataset.vipCode || '';
      var vvipCode = wrapper.dataset.vvipCode || '';

      // Reset to default first so this is safe to call repeatedly on the same wrapper.
      wrapper.querySelectorAll('.rc-cart-compare, .rc-cart-sale, .rc-cart-vip, .rc-cart-vvip').forEach(function (el) {
        el.classList.add('is-hide');
      });
      var regularEl = wrapper.querySelector('.rc-cart-regular');
      if (regularEl) regularEl.classList.remove('is-hide');

      var priceToShow = null;
      if (compare !== 0 && compare > regular) {
        priceToShow = '.rc-cart-compare';
      } else if (sale !== 0 && sale < regular && showSale) {
        priceToShow = '.rc-cart-sale';
      } else if (vip !== 0 && vip < regular && showVip && saleCode === vipCode) {
        priceToShow = '.rc-cart-vip';
      } else if (vvip !== 0 && vvip < regular && showVvip && saleCode === vvipCode) {
        priceToShow = '.rc-cart-vvip';
      }

      if (!priceToShow) return;

      var saleEl = wrapper.querySelector(priceToShow);
      if (saleEl) {
        if (regularEl) regularEl.classList.add('is-hide');
        saleEl.classList.remove('is-hide');
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    rcApplyCartPriceTiers();

    if (typeof webvista !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        rcApplyCartPriceTiers();
      });
    }

    // Fallback safety net for any cart re-render path that doesn't publish cartUpdate.
    var cartItemsContainer =
      document.getElementById('main-cart-items') ||
      document.querySelector('cart-items') ||
      document.querySelector('[data-cart-items]');

    if (cartItemsContainer && 'MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        rcApplyCartPriceTiers(cartItemsContainer);
      });
      observer.observe(cartItemsContainer, { childList: true, subtree: true });
    }
  });
}